import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';

import '../styles/styles.css';
import '../styles/formularios.css';
import 'bootstrap/dist/css/bootstrap.min.css';

import logoInstituto from '../components/images/logo.png';
// Tu HTML usaba "SEP_Logo_2019.svg.png.jpg"; si tu archivo real es .png.jpg, deja esa ruta.
// Si tu imagen correcta es .png, cambia la importación:
import logoSEP from '../components/images/SEP_Logo_2019.svg.png.jpg';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from '../utils/apiBase';


const ConsultarMaterias = () => {
  const [materias, setMaterias] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return trimmed ? JSON.parse(trimmed) : {};
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);
  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchJsonWithFallback('/materias/consultar-todas');
        const arr = Array.isArray(data) ? data : (data?.data || []);
        setMaterias(arr);
      } catch (error) {
        console.error('Error al consultar materias:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'No se pudieron cargar las materias.'
        });
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [fetchJsonWithFallback]);

  // Helper para grado (el SP puede regresar "Grado Asignado" o "GradoAsignado")
  const gradoDe = (m) =>
    m['Grado Asignado'] ?? m.GradoAsignado ?? '';

  // Helper para status (si viene 'A'/'I', mostramos legible)
  const statusDe = (m) => {
    const s = m.Status;
    if (s === 'A') return 'Activo';
    if (s === 'I') return 'Inactivo';
    return s ?? '';
  };

  return (
    <div className="container py-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="text-center flex-grow-1">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Consultar Materias</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Tabla de materias */}
      <div className="table-responsive mt-4">
        <table className="table table-bordered table-hover shadow-sm bg-white">
          <thead className="table-light text-center">
            <tr>
              <th>#</th>
              <th>Nombre</th>
              <th>Categoría</th>
              <th>Grado Asignado</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody id="tablaMaterias" className="text-center">
            {loading ? (
              <tr>
                <td colSpan={5} className="text-muted">Cargando...</td>
              </tr>
            ) : materias.length === 0 ? (
              <tr>
                <td colSpan={5}>No se encontraron materias</td>
              </tr>
            ) : (
              materias.map((materia, index) => (
                <tr key={`${materia.IdMateria ?? materia.Nombre}-${index}`}>
                  <td>{index + 1}</td>
                  <td>{materia.Nombre}</td>
                  <td>{materia.Categoria}</td>
                  <td>{gradoDe(materia)}</td>
                  <td>{statusDe(materia)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Botón salir */}
      <div className="mt-4 text-center">
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
             Salir
        </button>
      </div>
    </div>
  );
};

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default ConsultarMaterias;
